import { createSelector } from '@reduxjs/toolkit';
import { activityCodesByName } from 'src/store/selectors/organization/activityCodes';
import { employeesNotDeleted, getOrganization } from 'src/store/selectors';
import type { OrganizationState } from 'src/store/types';
import type { EmployeeViewModel } from '@trinnvis/dabih-api-client';

interface ProfessionInformation {
  name: string;
  defaultFunction?: string;
  defaultFunctionTemplateId?: number;
  specialities?: string[];
}

const professionsForHealth: ProfessionInformation[] = [
  { name: 'Akupunktør' },
  { name: 'Assistent' },
  { name: 'Audiograf' },
  { name: 'Bioingeniør' },
  { name: 'Ergoterapeut' },
  { name: 'Ernæringsfysiolog' },
  {
    name: 'Fysioterapeut',
    defaultFunction: 'Fysioterapeut',
    defaultFunctionTemplateId: 3784,
    specialities: [
      'Ingen',
      'Allmenn fysioterapi',
      'Barne- og ungdomsfysioterapi',
      'Helse- og miljøarbeid',
      'Idrettsfysioterapi',
      'Manuellterapi',
      'Onkologisk fysioterapi',
      'Psykomotorisk fysioterapi',
      'Psykiatrisk og psykosomatisk fysioterapi',
      'Geriatrisk fysioterapi',
      'Hjerte- og lungefysioterapi',
      'Nevrologisk fysioterapi',
      'Ortopedisk fysioterapi',
      'Obstetrisk og gynekologisk fysioterapi',
      'Revmatologisk fysioterapi',
    ],
  },
  { name: 'Helsefagarbeider' },
  { name: 'Helsesekretær' },
  { name: 'Helsesykepleier' },
  { name: 'Helsesøster' },
  { name: 'Hjelpepleier' },
  { name: 'Instruktør' },
  { name: 'Jordmor' },
  {
    name: 'Kiropraktor',
    defaultFunction: 'Kiropraktor',
    defaultFunctionTemplateId: 3775,
  },
  {
    name: 'Naprapat',
    defaultFunction: 'Naprapat',
    defaultFunctionTemplateId: 11281,
  },
  { name: 'Kontormedarbeider' },
  {
    name: 'Lege',
    defaultFunction: 'Lege',
    defaultFunctionTemplateId: 3765,
    specialities: [
      'Ingen',
      'Allmennmedisin',
      'Anestesiologi',
      'Arbeidsmedisin',
      'Barne- og ungdomspsykiatri',
      'Barnekirurgi',
      'Barnesykdommer',
      'Blodsykdommer',
      'Bryst- og endokrinkirurgi',
      'Endokrinologi',
      'Fordøyelsessykdommer',
      'Fysikalsk medisin og rehabilitering',
      'Fødselshjelp og kvinnesykdommer',
      'Gastroenterologisk kirurgi',
      'Generell kirurgi',
      'Geriatri',
      'Hjertesykdommer',
      'Hud- og veneriske sykdommer',
      'Immunologi og transfusjonsmedisin',
      'Indremedisin',
      'Infeksjonssykdommer',
      'Karkirurgi',
      'Klinisk farmakologi',
      'Klinisk nevrofysiologi',
      'Lungesykdommer',
      'Maxillofacial kirurgi',
      'Medisinsk biokjemi',
      'Medisinsk genetikk',
      'Medisinsk mikrobiologi',
      'Nevrokirurgi',
      'Nevrologi',
      'Nukleærmedisin',
      'Nyresykdommer',
      'Onkologi',
      'Ortopedisk kirurgi',
      'Patologi',
      'Plastikkirurgi',
      'Psykiatri',
      'Radiologi',
      'Revmatologi',
      'Rus- og avhengighetsmedisin',
      'Samfunnsmedisin',
      'Thoraxkirurgi',
      'Urologi',
      'Øre-nese-halssykdommer',
      'Øyesykdommer',
    ],
  },
  {
    name: 'LIS 1',
    defaultFunction: 'Lege',
    defaultFunctionTemplateId: 3765,
  },
  { name: 'Muskelterapeut' },
  {
    name: 'Osteopat',
    defaultFunction: 'Osteopat',
    defaultFunctionTemplateId: 10786,
  },
  { name: 'Optiker' },
  {
    name: 'Psykolog',
    defaultFunction: 'Psykolog',
    defaultFunctionTemplateId: 3769,
    specialities: [
      'Ingen',
      'Barne- og ungdomspsykologi',
      'Arbeidspsykologi',
      'Eldrepsykologi',
      'Organisasjonspsykologi',
      'Voksenpsykologi',
      'Habiliteringspsykologi',
      'Familiepsykologi',
      'Nevropsykologi',
      'Psykoterapi',
      'Rus- og avhengighetspsykologi',
      'Samfunns- og allmennpsykologi',
    ],
  },
  { name: 'Rengjører' },
  { name: 'Sekretær' },
  { name: 'Student' },
  { name: 'Sykepleier' },
  {
    name: 'Tannhelsesekretær',
    defaultFunction: 'Tannbehandlingsassistent',
    defaultFunctionTemplateId: 7079,
  },
  {
    name: 'Tannlege',
    defaultFunction: 'Tannlege',
    defaultFunctionTemplateId: 3777,
  },
  {
    name: 'Tannpleier',
    defaultFunction: 'Tannpleier',
    defaultFunctionTemplateId: 7063,
  },
  { name: 'Trener' },
  { name: 'Turnus' },
  {
    name: 'Turnuslege',
    defaultFunction: 'Lege',
    defaultFunctionTemplateId: 3765,
  },
  { name: 'Vernepleier' },
];

const professionsForHairdressing: ProfessionInformation[] = [
  { name: 'Frisør' },
  { name: 'Frisørlærling' },
  { name: 'Ryddehjelp/ekstahjelp' },
  { name: 'Hudpleier/kosmetolog' },
];

const professionsForCleaning: ProfessionInformation[] = [{ name: 'Teamleder' }, { name: 'Renholder' }];

const professionsForHotel: ProfessionInformation[] = [
  { name: 'Hotelldirektør' },
  { name: 'Driftsleder' },
  { name: 'Salgs- og markedssjef' },
  { name: 'Renholdsleder' },
  { name: 'Resepsjonssjef' },
  { name: 'Resepsjonist' },
  { name: 'Vaktmester' },
];

const professionsForRestaurant: ProfessionInformation[] = [
  { name: 'Restaurantsjef' },
  { name: 'Kjøkkensjef' },
  { name: 'Soussjef' },
  { name: 'Kokk' },
  { name: 'Kjøkkenassistent' },
  { name: 'Servitør' },
  { name: 'Sommelier' },
  { name: 'Barsjef' },
  { name: 'Bartender' },
  { name: 'Kokkelærling' },
  { name: 'Servitørlærling' },
  { name: 'Runner' },
];

export const professionsInformationMap: Map<string, ProfessionInformation> = new Map(
  professionsForHealth.map((i) => [i.name, i]),
);

export function specialitiesForProfession(p: string): string[] {
  const v = professionsInformationMap.get(p);
  return v?.specialities ?? [];
}

export function professionHasSpeciality(p: string): boolean {
  return specialitiesForProfession(p).length > 0;
}

function normalizeProfessions(list: ProfessionInformation[]): string[] {
  const v = list.map((i) => i.name);
  v.sort();
  v.push('Annet');
  return v;
}

export const professionsForHealthNormalized: string[] = normalizeProfessions(professionsForHealth);

export const professions = createSelector(
  getOrganization,
  employeesNotDeleted,
  (organization: OrganizationState | undefined, employees: EmployeeViewModel[]): string[] => {
    let professions: ProfessionInformation[] = [];
    if (organization !== undefined) {
      if (organization.sector === 'HEALTH') {
        professions = professionsForHealth;
      }
      if (organization.activityCodes.includes(activityCodesByName.hairdressing)) {
        professions = professionsForHairdressing;
      }
      if (organization.activityCodes.includes(activityCodesByName.cleaning)) {
        professions = professions.concat(professionsForCleaning);
      }
      if (organization.activityCodes.includes(activityCodesByName.hotel)) {
        professions = professions.concat(professionsForHotel);
      }
      if (organization.activityCodes.includes(activityCodesByName.restaurant)) {
        professions = professions.concat(professionsForRestaurant);
      }
      const professionNames: string[] = professions.map((p) => p.name);
      employees.forEach((e) => {
        if (e.profession && !professionNames.includes(e.profession)) {
          professionNames.push(e.profession);
        }
      });
      const result: string[] = [];
      professionNames.forEach((item) => {
        if (!result.includes(item)) {
          result.push(item);
        }
      });
      result.sort();
      return result;
    }
    return [];
  },
);
